import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { useEjercicio } from "@/context/EjercicioContext";
import { api } from "@/lib/api";

function fmt(iso) {
  const d = new Date(iso);
  const fecha = d.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const hora = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  return { fecha: fecha.charAt(0).toUpperCase() + fecha.slice(1), hora };
}

export default function Sesion() {
  const navigate = useNavigate();
  const { data } = useEjercicio();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await api.disponibilidad();
    setLoading(false);
    if (res.ok) setSlots(res.data.slots);
    else setError(res.error);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const solicitar = async () => {
    if (!selected) {
      setError("Elige un momento que te venga bien.");
      return;
    }
    setSending(true);
    setError("");
    const res = await api.crearSolicitud({ lead_id: data.leadId, fecha_sesion: selected });
    setSending(false);
    if (res.ok) {
      setDone(true);
    } else {
      setError(res.error);
      if (res.error.includes("disponible")) load();
    }
  };

  if (!data.leadId) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <main className="wrap-narrow py-20 text-center">
          <h1 className="font-display text-3xl text-[#102B5B]" style={{ fontWeight: 600 }}>
            Antes guarda tu paso
          </h1>
          <p className="mt-4 text-lg text-[#343A46]">
            Deja tus datos y tu primer paso para poder solicitar la sesión.
          </p>
          <Link
            to="/claridad"
            className="mt-7 inline-block rounded-full bg-[#275FBE] px-7 py-3.5 font-semibold text-white"
            data-testid="sesion-go-claridad"
          >
            Empezar el ejercicio
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <main className="wrap-narrow py-10 sm:py-14">
        <p className="eyebrow mb-4">Sesión de claridad</p>
        <h1
          className="font-display text-4xl text-[#102B5B] sm:text-5xl"
          style={{ fontWeight: 600 }}
          data-testid="sesion-title"
        >
          Hablemos de ese paso.
        </h1>
        <p className="mt-5 text-lg text-[#343A46]">
          Esta conversación sirve para entender dónde estás y qué necesitas para avanzar.
        </p>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 soft-card p-8 text-center"
            data-testid="sesion-success"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1E6A45]">
              <Check className="text-white" size={28} />
            </span>
            <h2 className="font-display mt-5 text-2xl text-[#102B5B]" style={{ fontWeight: 600 }}>
              Tu solicitud está registrada.
            </h2>
            <p className="mt-3 text-lg text-[#343A46]">
              Te confirmaremos la sesión por email. Mientras, puedes seguir avanzando.
            </p>
            <button
              type="button"
              onClick={() => navigate("/area")}
              className="mt-7 rounded-full bg-[#275FBE] px-8 py-3.5 text-base font-semibold text-white"
              data-testid="sesion-area"
            >
              Ir a mi espacio
            </button>
          </motion.div>
        ) : (
          <div className="mt-10">
            {loading ? (
              <p className="text-lg text-[#626B79]" data-testid="sesion-loading">
                Comprobando disponibilidad…
              </p>
            ) : slots.length === 0 ? (
              <p className="text-lg text-[#626B79]" data-testid="sesion-empty">
                Elige un momento que te venga bien. Ahora mismo no hay huecos; vuelve pronto.
              </p>
            ) : (
              <>
                <p className="mb-4 text-base text-[#626B79]">Elige un momento que te venga bien.</p>
                <div className="grid gap-3 sm:grid-cols-2" data-testid="sesion-slots">
                  {slots.map((s) => {
                    const { fecha, hora } = fmt(s);
                    const active = selected === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          setSelected(s);
                          setError("");
                        }}
                        data-testid={`sesion-slot-${s}`}
                        className={`rounded-2xl border p-4 text-left transition-colors duration-150 ${
                          active
                            ? "border-[#275FBE] bg-[#E6EEFA]"
                            : "border-[#D6DAE0] bg-white hover:border-[#C5D7F4]"
                        }`}
                      >
                        <span className="block text-base font-semibold text-[#171A20]">{fecha}</span>
                        <span className="text-sm text-[#626B79]">{hora}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {error && (
              <p className="field-error mt-5" data-testid="sesion-error">
                ⚠ {error}
              </p>
            )}

            <button
              type="button"
              onClick={solicitar}
              disabled={sending || loading}
              data-testid="sesion-solicitar"
              className="mt-8 w-full rounded-full bg-[#275FBE] py-4 text-base font-semibold text-white transition-transform duration-150 hover:scale-[1.01] active:scale-95 disabled:opacity-70 sm:w-auto sm:px-10"
            >
              {sending ? "Enviando…" : "Solicitar sesión"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
